import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import JsonLd from "@/components/feature/JsonLd";
import { getArticleSchema } from "@/seo/schema";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/categories";

const sections = [
  { h2: "article.h2.1", p: "article.p.1" },
  { h2: "article.h2.2", p: "article.p.2" },
  { h2: "article.h2.3", p: "article.p.3" },
  { h2: "article.h2.4", p: "article.p.4" },
  { h2: "article.h2.5", p: "article.p.5" },
  { h2: "article.h2.6", p: "article.p.6" },
];

const tableRows = [
  { c1: "article.table.r1c1", c2: "article.table.r1c2", c3: "article.table.r1c3" },
  { c1: "article.table.r2c1", c2: "article.table.r2c2", c3: "article.table.r2c3" },
  { c1: "article.table.r3c1", c2: "article.table.r3c2", c3: "article.table.r3c3" },
  { c1: "article.table.r4c1", c2: "article.table.r4c2", c3: "article.table.r4c3" },
];

const faqs = [
  { q: "article.faq.1.q", a: "article.faq.1.a" },
  { q: "article.faq.2.q", a: "article.faq.2.a" },
  { q: "article.faq.3.q", a: "article.faq.3.a" },
  { q: "article.faq.4.q", a: "article.faq.4.a" },
];

export default function ArticlePage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <>
      <JsonLd
        data={getArticleSchema({
          headline: t("article.title"),
          description: t("article.subtitle"),
        })}
      />
      <Navbar />
      <main className="pt-24">
        <article className="mx-auto max-w-3xl px-4 py-8 md:px-6">
          {/* 面包屑（含回主页内链） */}
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-foreground-900">
              {t("article.breadcrumbHome")}
            </Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-700">{t("article.breadcrumbCurrent")}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-900">
              {t("article.category")}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {t("article.title")}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
              {t("article.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {t("article.author")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {t("article.published")}: 2026
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {t("article.readingTime")}
              </span>
            </div>
          </header>

          <p className="mt-8 text-base leading-relaxed text-foreground-700 md:text-lg">
            {t("article.intro")}
          </p>

          {sections.map((s) => (
            <section key={s.h2} className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {t(s.h2)}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t(s.p)}</p>
            </section>
          ))}

          {/* 对比表 */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.table.title")}
            </h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[480px] text-sm">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 font-semibold text-foreground-900">
                      {t("article.table.h1")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-foreground-900">
                      {t("article.table.h2")}
                    </th>
                    <th className="px-4 py-3 font-semibold text-foreground-900">
                      {t("article.table.h3")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r) => (
                    <tr key={r.c1} className="border-t border-background-200">
                      <td className="px-4 py-3 font-semibold text-foreground-900">{t(r.c1)}</td>
                      <td className="px-4 py-3 text-foreground-600">{t(r.c2)}</td>
                      <td className="px-4 py-3 text-foreground-600">{t(r.c3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 文章 FAQ */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.faq.title")}
            </h2>
            <div className="mt-4 space-y-3">
              {faqs.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={f.q}
                    className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-semibold text-foreground-900">{t(f.q)}</span>
                      <span className="flex w-6 h-6 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                        <i className={isOpen ? "ri-subtract-line" : "ri-add-line"}></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="border-t border-background-200 px-5 py-4">
                        <p className="text-sm leading-relaxed text-foreground-600">{t(f.a)}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* 相关资源 + 内链 */}
          <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              {t("article.related.title")}
            </h2>
            <div className="mt-4 flex flex-col gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-900"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                {t("article.related.back")}
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 transition-colors hover:text-foreground-950"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-grid-line"></i>
                </span>
                {t("article.related.categories")}
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 transition-colors hover:text-foreground-950"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line"></i>
                </span>
                {t("article.related.main")}
              </a>
            </div>
          </section>
        </article>

        {/* CTA */}
        <section className="mx-auto max-w-3xl px-4 pb-16 md:px-6">
          <div className="rounded-2xl bg-primary-500 px-6 py-10 text-center md:px-12">
            <h2 className="font-heading text-2xl font-bold text-background-50">
              {t("article.cta.title")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-background-50/90">
              {t("article.cta.text")}
            </p>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-100"
            >
              {t("article.cta.button")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line"></i>
              </span>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}